import React, {useEffect, useState} from "react";
import {pageAuthorizedByGroup} from "../../fragments/PageAuthorization";
import {Link, useParams} from "react-router-dom";
import {
	Box,
	Breadcrumbs,
	CircularProgress,
	FormControl,
	InputLabel, MenuItem,
	Paper, Select,
	Stack,
	TextField,
	Typography
} from "@mui/material";
import {PentaSplit} from "../../../components/common/PentaSplit";
import {ProcessButton} from "../../../components/common/input/ProcessButton";
import {localStorageOfString} from "../../../libraries/util/LocalStorage";
import {QueryResult, useQuery} from "@apollo/client";
import {graphQLServices} from "../../../services/GraphQLServices";
import {productsEditorDefaultTargetStage, productsEditorTargetStage} from "../ProductModuleOverview";
import {
	ADD_PRODUCT_INCENTIVE_CODES,
	PRODUCT_INCENTIVE_DETAILS,
	PRODUCT_INCENTIVE_DICTIONARY, PRODUCT_INCENTIVE_SUMMARY,
	PUT_PRODUCT_INCENTIVE_DETAILS
} from "../Queries";
import {ProductIncentiveDetails} from "../models/ProductIncentiveDetails";
import {deepClone, normalizeData} from "../../../libraries/util/Functions";
import {cloneMutationData} from "../Utils";
import {ProductIncentiveDetailsKrn} from "../../../libraries/krn/Krn";
import {LazyAccordion} from "../../../components/common/containers/LazyAccordion";
import {Upload} from "@mui/icons-material";
import {productIncentiveTypes} from "../models/ProductIncentiveType";
import {translateFunction} from "../../../localization/TranslationService";
import {incentiveStatuses} from "../Data";
import {CurrentStageChip, HeaderSlot, StageSwitch} from "../../../components/onsite/Footer";
import {GlobalState} from "../../../global/GlobalState";
import {targetStageSafetyCheck} from "../../../libraries/util/SafetyCheck";

const translate = translateFunction("SureBasePortalProductsCatalog");

export const newIncentive = localStorageOfString("products:incentives:newIncentive");

export const IncentiveDetails = pageAuthorizedByGroup(function () {
	const [tenant, incentiveTypeString] = useParams()?.incentiveId?.split(":") ?? [];
	const krn = new ProductIncentiveDetailsKrn(tenant, incentiveTypeString).fullKrn;
	const incentiveType = productIncentiveTypes
		.filter(it => it.toLowerCase().replaceAll("_", "") === incentiveTypeString)
		.firstOrNull();
	
	const [timestamp, setTimestamp] = useState(0);
	
	const stage = GlobalState.targetKiziStage(productsEditorDefaultTargetStage);
	const disabled = stage !== productsEditorTargetStage;
	
	const incentiveQuery = useQuery(PRODUCT_INCENTIVE_DETAILS, {
		client: graphQLServices.productsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage,
			krn
		}
	});
	
	const incentiveDictionaryQuery = useQuery(PRODUCT_INCENTIVE_DICTIONARY, {
		client: graphQLServices.productsClient,
		fetchPolicy: "no-cache",
		variables: {
			krn,
			timestamp
		}
	});
	
	const incentiveSummaryQuery = useQuery(PRODUCT_INCENTIVE_SUMMARY, {
		client: graphQLServices.productsClient,
		fetchPolicy: "no-cache",
		variables: {
			productIncentiveDetailsKrn: [krn],
			timestamp
		}
	});
	
	const [incentive, setIncentive] = useState<ProductIncentiveDetails>();
	const [isNew, setIsNew] = useState(() => krn === newIncentive.get());
	
	// reset the state when stage changes
	// todo change this to proper effect that sets the state instead of clears the state, after removing the newProduct feature
	useEffect(() => {
		setIncentive(undefined);
	}, [stage]);
	
	const error = incentiveQuery.error || incentiveDictionaryQuery.error;
	if (error) {
		throw error;
	}
	
	const loading = incentiveQuery.loading;
	if (loading)
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	
	if (!incentive) {
		setIncentive(newIncentive.get() === krn
			? {
				id: crypto.randomUUID(),
				krn,
				clientName: tenant,
				productIncentiveType: incentiveType,
				state: "ACTIVE",
				label: ""
			}
			: incentiveQuery.data.productIncentiveDetails.nodes
				.map(deepClone)
				.map(normalizeData)[0]);
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	}
	
	async function saveChanges(productIncentiveDetails: ProductIncentiveDetails) {
		targetStageSafetyCheck(stage);
		
		await graphQLServices.productsClient.mutate({
			mutation: PUT_PRODUCT_INCENTIVE_DETAILS,
			variables: {
				stage,
				productIncentiveDetails: cloneMutationData(productIncentiveDetails),
			}
		});
		newIncentive.set(null);
		setIsNew(false);
	}
	
	async function uploadCodes(codes: string[]) {
		await graphQLServices.productsClient.mutate({
			mutation: ADD_PRODUCT_INCENTIVE_CODES,
			variables: {
				productIncentiveDetailsKrn: krn,
				codes: codes,
			}
		});
		setTimestamp(timestamp + 1);
	}
	
	const props = {incentive, setIncentive, incentiveDictionaryQuery, uploadCodes, isNew, incentiveSummaryQuery, disabled};
	return <>
		<HeaderSlot><StageSwitch defaultTargetStage={productsEditorDefaultTargetStage}/></HeaderSlot>
		<PentaSplit
			top={<Breadcrumbs style={{margin: "5px"}}>
				<Link style={{color: "inherit", textDecoration: "unset"}} to="/products">{translate("Products")}</Link>
				<Link style={{color: "inherit", textDecoration: "unset"}}
					to="/products/incentives">{translate("Incentives")}</Link>
				<Typography color="text.primary">{tenant}:{incentiveTypeString}</Typography>
			</Breadcrumbs>}
			center={<Paper style={{
				height: "100%",
			}}>
				<Stack style={{height: "100%"}}>
					<Information {...props}/>
					<div style={{flexGrow: 0, padding: "10px"}}>
						<ProcessButton
							buttonProps={{disabled}}
							defaultText={translate("Save")}
							inProgressText={translate("Saving")}
							completedText={translate("Saved")}
							resetKeys={[incentive]}
							variant={"outlined"}
							onClick={() => saveChanges(incentive)}
						/>
					</div>
				</Stack>
			</Paper>}
		/>
	</>;
}, ["Admin", "Product"]);

interface InformationProps {
	readonly incentive: ProductIncentiveDetails;
	readonly setIncentive: (productIncentiveDetails: ProductIncentiveDetails) => void;
	readonly incentiveDictionaryQuery: QueryResult<any, { timestamp: number; krn: string }>;
	readonly incentiveSummaryQuery: QueryResult<any, { timestamp: number; productIncentiveDetailsKrn: string[] }>;
	readonly uploadCodes: (codes: string[]) => Promise<any>;
	readonly isNew: boolean;
	readonly disabled?: boolean;
}

function Information(props: InformationProps) {
	return <div style={{height: "100%", overflow: "auto"}}>
		<Stack spacing={2} sx={{p: 3}}>
			<div>{translate("Product Incentive")}</div>
			<TextField
				label={translate("Kizi Resource Name")}
				value={props.incentive.krn}
				variant={"standard"}
				disabled
			/>
			<TextField
				label={translate("Client Name")}
				value={props.incentive.clientName}
				variant={"standard"}
				disabled
			/>
			<TextField
				label={translate("Incentive Type")}
				value={props.incentive.productIncentiveType}
				variant={"standard"}
				disabled
			/>
			<TextField
				label={translate("Label")}
				value={props.incentive.label ?? ""}
				variant={"standard"}
				disabled={props.disabled}
				onChange={event => {
					const newIncentive = {...props.incentive, label: event.target.value};
					props.setIncentive(newIncentive);
				}}
			/>
			<FormControl>
				<InputLabel id={"state"}>{translate("State")}</InputLabel>
				<Select
					labelId={"state"}
					label={translate("State")}
					variant={"standard"}
					value={props.incentive.state ?? ""}
					disabled={props.disabled}
					onChange={event => {
						const newIncentive = {...props.incentive, state: event.target.value};
						props.setIncentive(newIncentive);
					}}
				>
					<MenuItem key={"null"} value={""} hidden={true} style={{display: "none"}}></MenuItem>
					{incentiveStatuses.map(it =>
						<MenuItem key={it.toLowerCase()}
							value={it.toUpperCase()}>{translate(it)}</MenuItem>)}
				</Select>
			</FormControl>
			<Summary {...props}/>
			<Dictionary {...props}/>
		</Stack>
	</div>;
}

interface SummaryProps {
	readonly incentiveSummaryQuery: QueryResult<any, { timestamp: number; productIncentiveDetailsKrn: string[] }>;
	readonly isNew: boolean;
}

function Summary(props: SummaryProps) {
	if (props.incentiveSummaryQuery.loading) {
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	}
	
	const summary = props.incentiveSummaryQuery.data.productIncentiveSummary[0];
	
	if (!summary) {
		return <></>;
	}
	const totalCodes = summary.totalCodes;
	const availableCodes = summary.availableCodes;
	return <Stack spacing={1}>
		<TextField
			label={translate("AvailableCodes")}
			value={availableCodes}
			variant={"standard"}
			disabled
		/>
		<TextField
			label={translate("TotalCodes")}
			value={totalCodes}
			variant={"standard"}
			disabled
		/>
	</Stack>;
}

interface DictionaryProps {
	readonly incentiveDictionaryQuery: QueryResult<any, { timestamp: number; krn: string }>;
	readonly uploadCodes: (codes: string[]) => Promise<any>;
	readonly isNew: boolean;
}

function Dictionary(props: DictionaryProps) {
	const [newCodes, setNewCodes] = useState<string>("");
	const [resetKey, setResetKey] = useState<number>(0);
	
	if (props.incentiveDictionaryQuery.loading) {
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	}
	
	const codes: string[] = props.incentiveDictionaryQuery.data.productIncentiveDictionary.nodes
		.map((it: any) => it.code);
	
	return <Stack spacing={2}>
		<TextField
			label={translate("New Codes")}
			value={newCodes}
			multiline
			onChange={event => {
				setNewCodes(event.target.value);
				setResetKey(resetKey + 1);
			}}
			disabled={props.isNew}
		/>
		<ProcessButton
			defaultText={<>{translate("Upload")}&nbsp;<CurrentStageChip size={"small"}/></>}
			completedText={<>{translate("Uploaded")}&nbsp;<CurrentStageChip size={"small"}/></>}
			inProgressText={<>{translate("Uploading")}&nbsp;<CurrentStageChip size={"small"}/></>}
			startIcon={<Upload/>}
			resetKeys={resetKey}
			onClick={async () => {
				const newCodeList = newCodes
					.split("\n")
					.map(it => it.trim())
					.filter(it => !!it);
				await props.uploadCodes(newCodeList);
				setNewCodes("");
			}}
			buttonProps={{
				disabled: props.isNew
			}}
		/>
		{codes.length === 0
			? <div>{translate("No codes available")}</div>
			: <LazyAccordion
				summary={<div>{translate("Codes:")} ({codes.length})</div>}
			>
				{codes.map((it, index) => {
					return <div key={index}>{it}</div>;
				})}
			</LazyAccordion>
		}
	</Stack>;
}

export function openProductIncentiveDetailsForNew(krn: string) {
	newIncentive.set(krn);
	openProductIncentiveDetailsFor(krn);
}

export function openProductIncentiveDetailsFor(krn: string) {
	window.location.href = linkProductIncentiveDetailsFor(krn);
}

export function linkProductIncentiveDetailsFor(krn: string): string {
	const {tenantName, productIncentiveType} = ProductIncentiveDetailsKrn.parse(krn);
	return `/products/incentives/${encodeURIComponent([tenantName, productIncentiveType].join(":"))}`;
}
